import {
  BaseTextFieldProps,
  Box,
  FilledInputProps,
  InputBaseComponentProps,
  InputProps,
  OutlinedInputProps,
  SlotProps,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormHelperText } from '@mui/material';
import { InputRules } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { camelCase } from '@/utils/stringCaser';

type CustomHelperTextProps = {
  errorMessage?: string;
  maxLength?: number;
  value: string;
  helperText?: string;
};

function CustomHelperText({
  errorMessage,
  maxLength,
  value,
  helperText,
}: CustomHelperTextProps) {
  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        margin: '2px',
        justifyContent: errorMessage ? 'space-between' : 'flex-end',
      }}>
      {errorMessage && (
        <FormHelperText
          sx={{
            flex: 1,
            width: '100%',
            margin: 0,
            marginLeft: '15px',
            color: 'error',
          }}>
          {errorMessage}
        </FormHelperText>
      )}
      {helperText && helperText.trim() !== '' && (
        <FormHelperText
          sx={{
            marginLeft: '5px',
            display: 'flex',
            flexWrap: 'nowrap',
            color: errorMessage ? 'error' : 'text.secondary',
          }}>
          {helperText}
        </FormHelperText>
      )}
      {maxLength && (
        <FormHelperText
          sx={{
            marginLeft: '5px',
            display: 'flex',
            flexWrap: 'nowrap',
            color: errorMessage ? 'error' : 'black',
          }}>
          {value?.length || 0}/{maxLength} characters
        </FormHelperText>
      )}
    </div>
  );
}

type SVTextInputProps = {
  name: string;
  label: string;
  hideLabel?: boolean;
  disabled?: boolean;
  inputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputProps>
    | undefined;
  htmlInputProps?: SlotProps<
    React.ElementType<
      InputBaseComponentProps | undefined,
      keyof React.JSX.IntrinsicElements
    >,
    InputBaseComponentProps,
    BaseTextFieldProps
  >;
  multiline?: boolean;
  multilineRows?: number;
  customOnChangeEvent?: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  rules?: InputRules;
  type?: React.HTMLInputTypeAttribute;
  valueFormatter?: (value: string) => void;
  helperText?: string;
};

export default function SVTextInput({
  name,
  label,
  hideLabel,
  disabled = false,
  inputProps,
  htmlInputProps,
  multiline = false,
  multilineRows,
  rules,
  type,
  valueFormatter,
  customOnChangeEvent,
  helperText,
}: SVTextInputProps) {
  const methods = useFormContext();
  const builtRulesObject = buildInputRulesObject(label, rules);

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={builtRulesObject}
      render={({
        field: { value, onChange, onBlur, ref },
        fieldState: { error },
      }) => (
        <Box sx={{ marginBottom: 2 }}>
          <TextField
            disabled={disabled}
            error={!!error?.message}
            id={camelCase(label)}
            slotProps={{ input: inputProps, htmlInput: htmlInputProps }}
            inputRef={ref}
            label={hideLabel ? '' : label}
            multiline={multiline}
            onChange={e => {
              if (customOnChangeEvent) {
                onChange(customOnChangeEvent(e));
                return;
              }
              onChange(e);
            }}
            onBlur={onBlur}
            rows={multilineRows}
            type={type}
            value={valueFormatter ? valueFormatter(value || '') : value || ''}
            sx={{ width: '100%' }}
          />
          <CustomHelperText
            errorMessage={error?.message}
            maxLength={rules?.maxLength}
            value={value}
            helperText={helperText}
          />
        </Box>
      )}
    />
  );
}
